import React, { useEffect, useRef, useState } from 'react';
import { CONSENT_LABEL, PRIVACY_NOTICE_URL, ConsentStatus } from '../../services/analytics/consent';
import './PrivacyConsent.css';

interface Props {
  status: ConsentStatus;
  saving: boolean;
  writeError: boolean;
  settingsOpen: boolean;
  onCloseSettings: () => void;
  onDecide: (granted: boolean) => Promise<void>;
  onRetry: () => Promise<void>;
}

export default function PrivacyConsent({
  status, saving, writeError, settingsOpen, onCloseSettings, onDecide, onRetry,
}: Props) {
  const [dismissed, setDismissed] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const promptOpen = status === 'unset' && !dismissed;
  const showSettings = settingsOpen || (writeError && !dismissed);
  const open = showSettings || promptOpen;

  useEffect(() => {
    if (writeError) setDismissed(false);
  }, [writeError]);

  useEffect(() => {
    if (open && !dialog.current?.open) dialog.current?.showModal();
    if (!open && dialog.current?.open) dialog.current.close();
  }, [open]);

  const close = () => {
    setDismissed(true);
    onCloseSettings();
  };

  return (
    <dialog ref={dialog} className="privacy-consent" aria-labelledby="privacy-heading"
      onCancel={close} onClose={close}>
      <button type="button" className="privacy-close" aria-label="Close" onClick={() => {
        dialog.current?.close();
        close();
      }}>X</button>
      <h2 id="privacy-heading">{showSettings ? 'Privacy settings' : 'Help improve Artstash'}</h2>
      {showSettings ? (
        <label className="privacy-choice">
          <input type="checkbox" checked={status === 'granted'}
            disabled={saving || status === 'loading' || status === 'error'}
            onChange={event => void onDecide(event.target.checked)} />
          {CONSENT_LABEL}
        </label>
      ) : <p>{CONSENT_LABEL}</p>}
      <a href={PRIVACY_NOTICE_URL} target="_blank" rel="noopener noreferrer">Privacy notice</a>
      {writeError && <p role="alert">Unable to save privacy preferences. Usage analytics are disabled.</p>}
      {writeError && <button type="button" disabled={saving} onClick={() => void onRetry()}>Retry</button>}
      {settingsOpen && status === 'error' && !writeError &&
        <p role="alert">Privacy preferences unavailable. Usage analytics are disabled.</p>}
      {!showSettings && (
        <div className="privacy-actions">
          <button type="button" disabled={saving} onClick={() => void onDecide(true)}>Allow</button>
          <button type="button" disabled={saving} onClick={() => void onDecide(false)}>Not now</button>
        </div>
      )}
    </dialog>
  );
}
